import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '@/lib/api';
import { guideForPath, KB_BASE } from '@/lib/guides';

/**
 * QBEE - the assistant on every page (owner, 11 Oct 2026: "every sections,
 * reports, settings should have (explain me this or how to read) ... a right
 * side dock ... with few relevant question for that section as suggestions,
 * or take user input text").
 *
 * This holds what the dock needs from anywhere in the app: whether it is open
 * (remembered per browser), the conversation (its turns, sent back as context
 * so a follow-up makes sense), and the page the person is on - its address,
 * the title its header shows, and the guide the knowledge base has for it -
 * so "explain this" starts from the right guide (routes/ask, Qbee's server).
 *
 *   const qbee = useQbee();
 *   qbee.ask('Why did revenue drop?')                       a question about this page
 *   qbee.ask('What does this show?', { section: 'Sent' })   about one section of it
 *   qbee.openDock() / closeDock() / toggle() / newConversation()
 */

const QbeeContext = createContext(null);
const OPEN_KEY = 'qbee.open';

const newId = () => `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
const guidePathOf = (url) => (url ? String(url).replace(KB_BASE, '') : null);

// What an answer said, as plain text, for the next question's context.
function answerText(data) {
  const p = (data && data.panel) || {};
  const parts = [p.summary?.text || data?.answer || ''];
  for (const e of p.explain || []) parts.push(e.text || '');
  return parts.filter(Boolean).join(' ').slice(0, 1500);
}

export function QbeeProvider({ children }) {
  const location = useLocation();
  const [open, setOpen] = useState(() => { try { return localStorage.getItem(OPEN_KEY) === '1'; } catch { return false; } });
  const [turns, setTurns] = useState([]);
  const [conversationId, setConversationId] = useState(newId);
  // Each page's title, by its address: a header sets its own once it mounts
  // (and a child's effect runs before this provider's, so nothing here may
  // clear it on navigation; a stale title simply belongs to another path).
  const [titles, setTitles] = useState({});
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const setPageTitle = useCallback((t) => setTitles((m) => (m[pathRef.current] === t ? m : { ...m, [pathRef.current]: t })), []);
  const pageTitle = titles[location.pathname] || null;
  const turnsRef = useRef(turns);
  turnsRef.current = turns;

  useEffect(() => { try { localStorage.setItem(OPEN_KEY, open ? '1' : '0'); } catch { /* private window */ } }, [open]);

  const page = useMemo(() => ({
    route: `${location.pathname}${location.search || ''}`,
    title: pageTitle,
    guide: guidePathOf(guideForPath(location.pathname)),
  }), [location.pathname, location.search, pageTitle]);

  const ask = useCallback(async (question, opts = {}) => {
    const q = String(question || '').trim();
    if (q.length < 3) return;
    setOpen(true);
    const id = newId();
    const asked = { ...page, section: opts.section || null };
    // The turns before this one, oldest first, as the server reads them.
    const history = turnsRef.current.filter((t) => t.status === 'done').slice(-4)
      .map((t) => ({ q: t.question, a: answerText(t.data) }));
    setTurns((ts) => [...ts, { id, question: q, status: 'pending', page: asked }]);
    try {
      const { data } = await api.post('/api/ask', { question: q, page: asked, history, conversation_id: conversationId }, { timeout: 150_000 });
      setTurns((ts) => ts.map((t) => (t.id === id ? { ...t, status: 'done', data } : t)));
    } catch (err) {
      const msg = err?.response?.data?.error || 'Qbee could not answer just now. Try again in a moment.';
      setTurns((ts) => ts.map((t) => (t.id === id ? { ...t, status: 'error', error: msg } : t)));
    }
  }, [page, conversationId]);

  const rate = useCallback(async (turnId, value) => {
    const t = turnsRef.current.find((x) => x.id === turnId);
    if (!t || !t.data || !t.data.id) return;
    setTurns((ts) => ts.map((x) => (x.id === turnId ? { ...x, rating: value } : x)));
    try { await api.post(`/api/ask/${t.data.id}/rating`, { rating: value }); } catch { /* a lost rating is not worth an error */ }
  }, []);

  const value = useMemo(() => ({
    open, turns, page, conversationId,
    ask, rate,
    openDock: () => setOpen(true),
    closeDock: () => setOpen(false),
    toggle: () => setOpen((v) => !v),
    newConversation: () => { setTurns([]); setConversationId(newId()); },
    setPageTitle,
  }), [open, turns, page, conversationId, ask, rate, setPageTitle]);

  return <QbeeContext.Provider value={value}>{children}</QbeeContext.Provider>;
}

export function useQbee() {
  return useContext(QbeeContext);
}

/** A page header tells Qbee what the page is called. */
export function useQbeePageTitle(title) {
  const q = useQbee();
  const set = q && q.setPageTitle;
  useEffect(() => { if (set && typeof title === 'string' && title) set(title); }, [set, title]);
}
